import { AxiosError } from "axios";
import { ErrorHandler } from "./error-handler";
import { curriedGet, curriedPost } from "./abort-controller";
import { HttpHeaders } from "../../../utils/http-utils";

export abstract class IValidator {
  abstract validate(service: ValidatorService);
}

export enum DiagnosticSeverity {
  ERROR,
  WARNING,
  INFO,
}
export class BaseDiagnostic {
  message: string;
  severity: DiagnosticSeverity;

  constructor(message: string, severity: DiagnosticSeverity) {
    this.message = message;
    if (severity === 0) this.severity = DiagnosticSeverity.INFO;
    if (severity === 1) this.severity = DiagnosticSeverity.WARNING;
    if (severity === 2) this.severity = DiagnosticSeverity.ERROR;
  }
}

export class IdentifiableDiagnostic extends BaseDiagnostic {
  elementId: string;
  elementName: string;
  constructor(id: string, message: string, severity: number) {
    super(message, severity);
    this.elementId = id;
  }
}

export class ValidatorService {
  errorHandler = new ErrorHandler("Validation service");

  private static readonly apiUrl = "/api/models";

  private readonly config: {
    baseURL: string;
    timeout: number;
    headers: HttpHeaders;
  };

  constructor(
    private baseURL: string,
    private validator: IValidator
  ) {
    this.config = {
      baseURL: this.baseURL,
      timeout: 3000,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };
  }

  validate() {
    this.validator.validate(this);
  }

  async validateModelById(
    modelId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    callback?: (diagnostics: IdentifiableDiagnostic[]) => Promise<any> | void,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    err?: (err: any) => void
  ) {
    const { apiUrl } = ValidatorService;

    const requestPromise = curriedGet(apiUrl + "/" + modelId + "/issues", this.config);
    return await requestPromise
      .then(async (res) => {
        const diagnostics = await this.processValidationResults(res);
        if (callback) await callback(diagnostics);
        return diagnostics;
      })

      .catch((e: string | AxiosError) => {
        if (typeof e === "string") {
          throw e;
        }
        if (err) err(e);
        throw this.errorHandler.handle(e, "validation-service");
      });
  }

  // used for CREATE VSCode Extension
  async validateModel(jsonModel: string) {
    const requestPromise = curriedPost("/api/validate", this.config, false, jsonModel);

    return await requestPromise
      .then(this.processValidationResults)

      .catch((e: string | AxiosError) => {
        if (typeof e === "string") {
          throw e;
        }
        throw this.errorHandler.handle(e, "Validation", "Unknown Error", true);
      });
  }

  private async processValidationResults(res: { data: { id: string; message: string; severity: number }[] }) {
    const diagnostics = res.data.map((d) => new IdentifiableDiagnostic(d.id, d.message, d.severity));

    return diagnostics;
  }
}
